export const salesAssistantAnalyticsDe = {
  title: 'Erfolg und Wirkung',
  subtitle:
    'Nachvollziehbare Kundenpflege-Kennzahlen – ohne Bildschirm-, Tasten- oder Aktivitätsüberwachung.',
  back: 'Zum Vertriebscockpit',
  filters: {
    scope: 'Auswertung',
    own: 'Meine Kundenpflege',
    team: 'Freigegebene Teams',
    dateFrom: 'Von',
    dateTo: 'Bis',
    teamLabel: 'Team',
    allTeams: 'Alle freigegebenen Teams',
    employeeLabel: 'Mitarbeiterdetail',
    aggregated: 'Keine Einzelansicht',
    employeeDisabled: 'Einzelansichten sind für diesen Mandanten nicht ausdrücklich freigegeben.',
  },
  cohortHelp:
    'Empfehlungsquoten beziehen sich auf Empfehlungen, die im gewählten Zeitraum entstanden sind. Aufgaben- und Kontaktergebnisse beziehen sich auf ihr jeweiliges Ereignisdatum.',
  metrics: {
    recommendationsGenerated: 'Erzeugte Empfehlungen',
    recommendationsProcessed: 'Bearbeitete Empfehlungen',
    overdueTasks: 'Überfällige Aufgaben',
    averageResponseHours: 'Ø Reaktionszeit',
    contactAttempts: 'Kontaktversuche',
    successfulContacts: 'Persönliche Kontakte',
    plannedCallbacks: 'Geplante Rückrufe',
    quotesCreated: 'Erzeugte Angebote',
    attributedOrders: 'Zeitlich zugeordnete Aufträge',
    attributedRevenue: 'Zeitlich zugeordneter Umsatz',
    reactivatedCustomers: 'Reaktivierte Kunden',
    reactivatedRevenue: 'Zugeordneter Reaktivierungsumsatz',
    irrelevantRecommendations: 'Als irrelevant bewertet',
    falsePositiveRatePct: 'Fehlalarmquote',
    hours: '{{value}} Std.',
  },
  ruleQuality: {
    title: 'Regelqualität',
    description:
      'Die Fehlalarmquote ist der Anteil „nicht relevant“ an den menschlich bearbeiteten Empfehlungen.',
    rule: 'Regel',
    generated: 'Erzeugt',
    processed: 'Bearbeitet',
    completed: 'Erledigt',
    irrelevant: 'Nicht relevant',
    orders: 'Zugeordnete Aufträge',
    falsePositive: 'Fehlalarmquote',
  },
  teams: {
    title: 'Aufgabenverteilung nach Team',
    team: 'Team',
    unassigned: 'Ohne Teamzuordnung',
    open: 'Offen',
    overdue: 'Überfällig',
    completed: 'Im Zeitraum erledigt',
  },
  trend: {
    title: 'Entwicklung der Inaktivitätssignale',
    description:
      'Gezählt werden Kunden mit neu erzeugter Reaktivierungs- oder Bestellrhythmus-Empfehlung, keine rückwirkend erfundenen Bestandswerte.',
  },
  attribution: {
    title: 'Prüfbare Umsatzzuordnung',
    description:
      'Ein Auftrag wird der letzten dokumentierten erfolgreichen Vertriebsaktivität desselben Kunden innerhalb von {{days}} Tagen zeitlich zugeordnet.',
    causality:
      'Diese Zuordnung zeigt eine zeitliche Nähe und behauptet keine sichere Ursache oder Mitarbeiterleistung.',
    customer: 'Kunde',
    activity: 'Aktivität',
    order: 'Auftrag',
    delay: 'Abstand',
    value: 'Wert',
    days: '{{count}} Tage',
    openCustomer: 'Kundenakte öffnen',
    openOrder: 'Auftrag prüfen',
  },
  privacy: {
    title: 'Transparenz der Datengrundlage',
    description:
      'Die Auswertung verwendet nur fachliche ERP-Ereignisse. Mitarbeiter sehen ihre eigene Grundlage; Einzelheiten anderer Personen erfordern zwei ausdrückliche Freigaben.',
    captured: 'Verwendet',
    excluded: 'Nicht erfasst oder bewertet',
    capturedFacts: {
      RECOMMENDATION_ASSIGNMENT_AND_STATUS: 'Zuweisung und Status von Empfehlungen',
      TASK_STATUS_AND_DUE_DATE: 'Aufgabenstatus und Fälligkeit',
      DOCUMENTED_CONTACT_OUTCOME: 'Bewusst dokumentiertes Kontaktergebnis',
      FOLLOW_UP_AND_QUOTE_CREATED: 'Erstellte Wiedervorlage oder Angebot',
      ORDER_TIMESTAMP_AND_VALUE: 'Zeitpunkt und Wert eines Auftrags',
    },
    excludedFacts: {
      KEYSTROKES: 'Tastatureingaben',
      SCREEN_TIME: 'Bildschirm- oder Anwesenheitszeit',
      UNRELATED_EMPLOYEE_ACTIVITY: 'Nicht vertriebsbezogene Mitarbeiteraktivität',
      SENSITIVE_TRAITS: 'Besonders sensible persönliche Merkmale',
    },
  },
  empty: 'Für diese Auswahl liegen keine Daten vor.',
  loadError: 'Die Vertriebsassistent-Auswertung konnte nicht geladen werden.',
  previous: 'Zurück',
  next: 'Weiter',
  page: 'Seite {{page}} von {{pages}}',
};

export const salesAssistantAnalyticsEn = {
  title: 'Outcome and impact',
  subtitle:
    'Explainable customer-care metrics without screen, keystroke, or activity surveillance.',
  back: 'Back to sales cockpit',
  filters: {
    scope: 'Analysis',
    own: 'My customer care',
    team: 'Shared teams',
    dateFrom: 'From',
    dateTo: 'To',
    teamLabel: 'Team',
    allTeams: 'All shared teams',
    employeeLabel: 'Employee detail',
    aggregated: 'No individual view',
    employeeDisabled: 'Individual views have not been explicitly enabled for this tenant.',
  },
  cohortHelp:
    'Recommendation rates use recommendations generated in the selected period. Task and contact results use their respective event dates.',
  metrics: {
    recommendationsGenerated: 'Recommendations generated',
    recommendationsProcessed: 'Recommendations handled',
    overdueTasks: 'Overdue tasks',
    averageResponseHours: 'Average response time',
    contactAttempts: 'Contact attempts',
    successfulContacts: 'Personal contacts',
    plannedCallbacks: 'Callbacks planned',
    quotesCreated: 'Quotes created',
    attributedOrders: 'Temporally attributed orders',
    attributedRevenue: 'Temporally attributed revenue',
    reactivatedCustomers: 'Reactivated customers',
    reactivatedRevenue: 'Attributed reactivation revenue',
    irrelevantRecommendations: 'Marked irrelevant',
    falsePositiveRatePct: 'False-positive rate',
    hours: '{{value}} h',
  },
  ruleQuality: {
    title: 'Rule quality',
    description:
      'The false-positive rate is the share marked irrelevant among recommendations handled by a person.',
    rule: 'Rule',
    generated: 'Generated',
    processed: 'Handled',
    completed: 'Completed',
    irrelevant: 'Irrelevant',
    orders: 'Attributed orders',
    falsePositive: 'False-positive rate',
  },
  teams: {
    title: 'Task distribution by team',
    team: 'Team',
    unassigned: 'No team assigned',
    open: 'Open',
    overdue: 'Overdue',
    completed: 'Completed in period',
  },
  trend: {
    title: 'Inactivity signal trend',
    description:
      'Counts customers with a newly generated reactivation or cadence recommendation; no historical snapshots are invented.',
  },
  attribution: {
    title: 'Reviewable revenue attribution',
    description:
      'An order is temporally attributed to the latest documented successful sales activity for the same customer within {{days}} days.',
    causality:
      'This attribution shows temporal proximity and does not claim certain causality or employee performance.',
    customer: 'Customer',
    activity: 'Activity',
    order: 'Order',
    delay: 'Delay',
    value: 'Value',
    days: '{{count}} days',
    openCustomer: 'Open customer file',
    openOrder: 'Review order',
  },
  privacy: {
    title: 'Data-basis transparency',
    description:
      'The analysis only uses business events in the ERP. Employees see their own basis; another person’s details require two explicit permissions.',
    captured: 'Used',
    excluded: 'Not captured or evaluated',
    capturedFacts: {
      RECOMMENDATION_ASSIGNMENT_AND_STATUS: 'Recommendation assignment and status',
      TASK_STATUS_AND_DUE_DATE: 'Task status and due date',
      DOCUMENTED_CONTACT_OUTCOME: 'Deliberately documented contact outcome',
      FOLLOW_UP_AND_QUOTE_CREATED: 'Created follow-up or quote',
      ORDER_TIMESTAMP_AND_VALUE: 'Order timestamp and value',
    },
    excludedFacts: {
      KEYSTROKES: 'Keystrokes',
      SCREEN_TIME: 'Screen or presence time',
      UNRELATED_EMPLOYEE_ACTIVITY: 'Unrelated employee activity',
      SENSITIVE_TRAITS: 'Special-category personal traits',
    },
  },
  empty: 'No data is available for this selection.',
  loadError: 'The sales-assistant analysis could not be loaded.',
  previous: 'Previous',
  next: 'Next',
  page: 'Page {{page}} of {{pages}}',
};

export const salesAssistantAnalyticsEs = {
  ...salesAssistantAnalyticsEn,
  title: 'Resultados e impacto',
  subtitle: 'Métricas explicables de atención al cliente sin vigilancia de pantalla o teclado.',
  back: 'Volver al asistente de ventas',
  filters: {
    ...salesAssistantAnalyticsEn.filters,
    scope: 'Evaluación',
    own: 'Mi atención al cliente',
    team: 'Equipos compartidos',
    dateFrom: 'Desde',
    dateTo: 'Hasta',
    teamLabel: 'Equipo',
    allTeams: 'Todos los equipos compartidos',
    employeeLabel: 'Detalle del empleado',
    aggregated: 'Sin vista individual',
    employeeDisabled: 'Las vistas individuales no están habilitadas para este cliente.',
  },
  ruleQuality: {
    ...salesAssistantAnalyticsEn.ruleQuality,
    title: 'Calidad de las reglas',
    description:
      'La tasa de falsos positivos es la proporción marcada como irrelevante entre las recomendaciones revisadas por una persona.',
  },
  teams: { ...salesAssistantAnalyticsEn.teams, title: 'Distribución de tareas por equipo' },
  trend: {
    title: 'Evolución de señales de inactividad',
    description:
      'Cuenta clientes con una nueva recomendación de reactivación o cadencia; no inventa datos históricos.',
  },
  attribution: {
    ...salesAssistantAnalyticsEn.attribution,
    title: 'Atribución de ingresos verificable',
    causality:
      'La atribución solo muestra proximidad temporal y no afirma causalidad ni rendimiento individual.',
  },
  privacy: {
    ...salesAssistantAnalyticsEn.privacy,
    title: 'Transparencia de los datos',
    description:
      'Solo se usan eventos comerciales del ERP; los detalles de otra persona exigen dos permisos explícitos.',
  },
  empty: 'No hay datos para esta selección.',
  loadError: 'No se pudo cargar la evaluación del asistente de ventas.',
  previous: 'Anterior',
  next: 'Siguiente',
  page: 'Página {{page}} de {{pages}}',
};

export const salesAssistantAnalyticsFr = {
  ...salesAssistantAnalyticsEn,
  title: 'Résultats et impact',
  subtitle: 'Indicateurs explicables de suivi client, sans surveillance de l’écran ou du clavier.',
  back: 'Retour à l’assistant commercial',
  filters: {
    ...salesAssistantAnalyticsEn.filters,
    scope: 'Analyse',
    own: 'Mon suivi client',
    team: 'Équipes partagées',
    dateFrom: 'Du',
    dateTo: 'Au',
    teamLabel: 'Équipe',
    allTeams: 'Toutes les équipes partagées',
    employeeLabel: 'Détail collaborateur',
    aggregated: 'Aucune vue individuelle',
    employeeDisabled: 'Les vues individuelles ne sont pas activées pour ce tenant.',
  },
  ruleQuality: {
    ...salesAssistantAnalyticsEn.ruleQuality,
    title: 'Qualité des règles',
    description:
      'Le taux de faux positifs est la part marquée non pertinente parmi les recommandations traitées humainement.',
  },
  teams: { ...salesAssistantAnalyticsEn.teams, title: 'Répartition des tâches par équipe' },
  trend: {
    title: 'Évolution des signaux d’inactivité',
    description:
      'Compte les clients avec un nouveau signal de réactivation ou de cadence, sans inventer d’historique.',
  },
  attribution: {
    ...salesAssistantAnalyticsEn.attribution,
    title: 'Attribution de chiffre d’affaires vérifiable',
    causality:
      'Cette attribution indique uniquement une proximité temporelle, sans affirmer de causalité ni de performance individuelle.',
  },
  privacy: {
    ...salesAssistantAnalyticsEn.privacy,
    title: 'Transparence des données',
    description:
      'Seuls les événements métier de l’ERP sont utilisés ; le détail d’une autre personne exige deux autorisations explicites.',
  },
  empty: 'Aucune donnée pour cette sélection.',
  loadError: 'Impossible de charger l’analyse de l’assistant commercial.',
  previous: 'Précédent',
  next: 'Suivant',
  page: 'Page {{page}} sur {{pages}}',
};

export const salesAssistantAnalyticsRu = {
  ...salesAssistantAnalyticsEn,
  title: 'Результаты и влияние',
  subtitle: 'Понятные показатели работы с клиентами без контроля экрана или клавиатуры.',
  back: 'Назад к помощнику продаж',
  filters: {
    ...salesAssistantAnalyticsEn.filters,
    scope: 'Анализ',
    own: 'Моя работа с клиентами',
    team: 'Доступные команды',
    dateFrom: 'С',
    dateTo: 'По',
    teamLabel: 'Команда',
    allTeams: 'Все доступные команды',
    employeeLabel: 'Детализация сотрудника',
    aggregated: 'Без персональной детализации',
    employeeDisabled: 'Персональная детализация не включена для этого клиента.',
  },
  ruleQuality: {
    ...salesAssistantAnalyticsEn.ruleQuality,
    title: 'Качество правил',
    description:
      'Доля ложных срабатываний — это рекомендации, отмеченные как неактуальные, среди обработанных человеком.',
  },
  teams: { ...salesAssistantAnalyticsEn.teams, title: 'Распределение задач по командам' },
  trend: {
    title: 'Динамика сигналов неактивности',
    description:
      'Учитываются клиенты с новым сигналом реактивации или ритма заказов; исторические данные не выдумываются.',
  },
  attribution: {
    ...salesAssistantAnalyticsEn.attribution,
    title: 'Проверяемая атрибуция выручки',
    causality:
      'Атрибуция показывает только близость по времени и не утверждает причинность или результативность сотрудника.',
  },
  privacy: {
    ...salesAssistantAnalyticsEn.privacy,
    title: 'Прозрачность данных',
    description:
      'Используются только бизнес-события ERP; детали другого сотрудника требуют двух явных разрешений.',
  },
  empty: 'Для выбранных условий данных нет.',
  loadError: 'Не удалось загрузить анализ помощника продаж.',
  previous: 'Назад',
  next: 'Далее',
  page: 'Страница {{page}} из {{pages}}',
};

export const salesAssistantAnalyticsTr = {
  ...salesAssistantAnalyticsEn,
  title: 'Sonuçlar ve etki',
  subtitle: 'Ekran veya klavye takibi olmadan açıklanabilir müşteri bakım ölçümleri.',
  back: 'Satış asistanına dön',
  filters: {
    ...salesAssistantAnalyticsEn.filters,
    scope: 'Analiz',
    own: 'Müşteri bakımım',
    team: 'Paylaşılan ekipler',
    dateFrom: 'Başlangıç',
    dateTo: 'Bitiş',
    teamLabel: 'Ekip',
    allTeams: 'Tüm paylaşılan ekipler',
    employeeLabel: 'Çalışan ayrıntısı',
    aggregated: 'Bireysel görünüm yok',
    employeeDisabled: 'Bireysel görünümler bu kiracı için açıkça etkinleştirilmemiştir.',
  },
  ruleQuality: {
    ...salesAssistantAnalyticsEn.ruleQuality,
    title: 'Kural kalitesi',
    description:
      'Yanlış pozitif oranı, bir kişi tarafından işlenen öneriler içinde ilgisiz işaretlenenlerin payıdır.',
  },
  teams: { ...salesAssistantAnalyticsEn.teams, title: 'Ekibe göre görev dağılımı' },
  trend: {
    title: 'Hareketsizlik sinyali eğilimi',
    description:
      'Yeni yeniden etkinleştirme veya sipariş ritmi önerisi bulunan müşteriler sayılır; geçmiş değerler uydurulmaz.',
  },
  attribution: {
    ...salesAssistantAnalyticsEn.attribution,
    title: 'İncelenebilir gelir ilişkilendirmesi',
    causality:
      'Bu ilişkilendirme yalnızca zamansal yakınlığı gösterir; kesin nedensellik veya çalışan performansı iddia etmez.',
  },
  privacy: {
    ...salesAssistantAnalyticsEn.privacy,
    title: 'Veri temeli şeffaflığı',
    description:
      'Yalnızca ERP iş olayları kullanılır; başka bir kişinin ayrıntıları iki açık izin gerektirir.',
  },
  empty: 'Bu seçim için veri yok.',
  loadError: 'Satış asistanı analizi yüklenemedi.',
  previous: 'Önceki',
  next: 'Sonraki',
  page: 'Sayfa {{page}} / {{pages}}',
};
